import { useState, useEffect } from 'react';
import { apiFetch } from '../utils';

export default function Billing() {
  const [text, setText] = useState('');
  const [users, setUsers] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [pendingUnmatched, setPendingUnmatched] = useState([]);
  const [associations, setAssociations] = useState({});
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesRefreshing, setInvoicesRefreshing] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateResults, setUpdateResults] = useState(null);

  const loadInvoices = () => {
    setInvoicesLoading(true);
    apiFetch('/getInvoices')
      .then(data => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoices([]))
      .finally(() => setInvoicesLoading(false));
  };

  useEffect(() => { loadInvoices(); }, []);

  useEffect(() => {
    if (!selectedInvoice) { setInvoiceDetail(null); return; }
    const id = selectedInvoice.split('/').pop();
    setInvoiceDetailLoading(true);
    apiFetch(`/getInvoice/${id}`)
      .then(data => setInvoiceDetail(data))
      .catch(() => setInvoiceDetail(null))
      .finally(() => setInvoiceDetailLoading(false));
  }, [selectedInvoice]);

  const handleRefreshInvoices = async () => {
    setInvoicesRefreshing(true);
    try {
      await apiFetch('/cacheInvoices');
      loadInvoices();
    } catch (err) {
      setError('Failed to refresh invoices: ' + err.message);
    } finally {
      setInvoicesRefreshing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePaste = async () => {
    const pasted = await navigator.clipboard.readText();
    setText(pasted);
  };

  const parseDiscordName = (line) => {
    const match = line.match(/^(\S+)/);
    return match ? match[1] : null;
  };

  const replaceInText = (rawText, allUsers, resolvedAssociations = {}) => {
    return rawText.split('\n').map(line => {
      const discordName = parseDiscordName(line);
      if (!discordName) return line;
      let user = allUsers.find(u => u.discord_user_name === discordName);
      if (!user && resolvedAssociations[discordName]) {
        user = allUsers.find(u => u.url === resolvedAssociations[discordName]);
      }
      if (!user) return line;
      return line.replace(discordName, `${user.first_name} ${user.last_name}`);
    }).join('\n');
  };

  const handleProcess = async () => {
    setProcessing(true);
    setError(null);
    try {
      const data = await apiFetch('/getUserData');
      const allUsers = (data && data.users) ? data.users : [];
      setUsers(allUsers);

      const unmatched = [];
      for (const line of text.split('\n')) {
        const discordName = parseDiscordName(line);
        if (!discordName) continue;
        const found = allUsers.find(u => u.discord_user_name === discordName);
        if (!found && !unmatched.includes(discordName)) {
          unmatched.push(discordName);
        }
      }

      if (unmatched.length > 0) {
        setPendingUnmatched(unmatched);
        setAssociations(Object.fromEntries(unmatched.map(n => [n, ''])));
        setResolving(true);
      } else {
        setText(replaceInText(text, allUsers));
      }
    } catch (err) {
      setError('Failed to process: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = async () => {
    setProcessing(true);
    setError(null);
    try {
      for (const [discordName, userUrl] of Object.entries(associations)) {
        if (!userUrl) continue;
        await apiFetch('/updateDiscordUserName', {
          method: 'POST',
          body: JSON.stringify({ userUrl, discord_user_name: discordName }),
        });
      }

      const data = await apiFetch('/getUserData');
      const allUsers = (data && data.users) ? data.users : [];

      setText(replaceInText(text, allUsers, associations));
      setResolving(false);
      setPendingUnmatched([]);
      setAssociations({});
      setUsers(allUsers);
    } catch (err) {
      setError('Failed to save associations: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const parseBillingLine = (line) => {
    const match = line.match(/^(.+?)\s+-\s+(\d+)\s+days/i);
    if (!match) return null;
    return { name: match[1].trim(), days: parseInt(match[2], 10) };
  };

  const handleUpdateInvoice = async () => {
    if (!invoiceDetail?.invoice_items?.length) return;
    setUpdating(true);
    setUpdateResults(null);
    const results = [];

    for (const line of text.split('\n')) {
      const parsed = parseBillingLine(line);
      if (!parsed) continue;

      const item = invoiceDetail.invoice_items.find(i =>
        i.description?.toLowerCase() === parsed.name.toLowerCase()
      );

      if (!item) {
        results.push({ name: parsed.name, days: parsed.days, success: false, reason: 'No matching line item found' });
        continue;
      }

      const id = item.url.split('/').pop();
      try {
        await apiFetch(`/updateInvoiceItem/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ quantity: parsed.days }),
        });
        results.push({ name: parsed.name, days: parsed.days, success: true });
      } catch (err) {
        results.push({ name: parsed.name, days: parsed.days, success: false, reason: err.message });
      }
    }

    setUpdateResults(results);
    setUpdating(false);

    // Reload invoice detail to reflect updated quantities
    if (selectedInvoice) {
      const id = selectedInvoice.split('/').pop();
      apiFetch(`/getInvoice/${id}`)
        .then(data => setInvoiceDetail(data))
        .catch(() => {});
    }
  };

  const invoiceLabel = (inv) =>
    `${inv.reference} — ${inv.contact_name} (${inv.dated_on})`;

  const fmt = (val) =>
    val != null ? `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
          <h2 className="flex-1 text-base font-semibold text-slate-900">Invoices</h2>
          <button
            onClick={handleRefreshInvoices}
            disabled={invoicesRefreshing || invoicesLoading}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <svg className={`h-4 w-4 ${invoicesRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {invoicesRefreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div className="p-5">
          <select
            value={selectedInvoice}
            onChange={e => setSelectedInvoice(e.target.value)}
            disabled={invoicesLoading}
            className="h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
          >
            <option value="">{invoicesLoading ? 'Loading…' : '— Select an invoice —'}</option>
            {invoices.map(inv => (
              <option key={inv.url} value={inv.url}>
                {invoiceLabel(inv)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
          <h2 className="flex-1 text-base font-semibold text-slate-900">Billing</h2>
          {!resolving && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleProcess}
                disabled={processing || !text.trim()}
                className="inline-flex h-9 items-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40"
              >
                {processing ? 'Processing…' : 'Process'}
              </button>
              <button
                onClick={handleUpdateInvoice}
                disabled={updating || !text.trim() || !invoiceDetail}
                className="inline-flex h-9 items-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
              >
                {updating ? 'Updating…' : 'Update Invoice'}
              </button>
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="relative">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={16}
            placeholder={"Paste billing data here, e.g.:\nandrew.williams - 21 days (12th sick)\ndavidw5127 - 22 days"}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-slate-300 resize-y"
          />
            <button
              onClick={handlePaste}
              title="Paste from clipboard"
              className="absolute right-20 top-2 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Paste
            </button>
            <button
              onClick={handleCopy}
              disabled={!text.trim()}
              title="Copy to clipboard"
              className="absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              {copied ? (
                <>
                  <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {updateResults && (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 text-xs font-medium text-slate-600 border-b border-slate-200">
                Update results — {updateResults.filter(r => r.success).length} succeeded, {updateResults.filter(r => !r.success).length} failed
              </div>
              <ul className="divide-y divide-slate-100">
                {updateResults.map((r, i) => (
                  <li key={i} className="flex items-center gap-3 px-4 py-2">
                    {r.success ? (
                      <svg className="h-4 w-4 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="flex-1 text-sm text-slate-800">
                      <span className="font-medium">{r.name}</span>
                      {r.success ? ` — updated to ${r.days} days` : ` — ${r.reason}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {resolving && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-medium text-slate-900">
                The following Discord usernames were not recognised. Select the matching team member for each:
              </p>
              <div className="space-y-2">
                {pendingUnmatched.map(discordName => (
                  <div key={discordName} className="flex items-center gap-3">
                    <span className="w-52 shrink-0 font-mono text-sm text-slate-700">{discordName}</span>
                    <select
                      value={associations[discordName] || ''}
                      onChange={e => setAssociations(prev => ({ ...prev, [discordName]: e.target.value }))}
                      className="h-9 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <option value="">— Select team member —</option>
                      {users.map(u => (
                        <option key={u.url} value={u.url}>
                          {u.first_name} {u.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleConfirm}
                  disabled={processing}
                  className="inline-flex h-9 items-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40"
                >
                  {processing ? 'Saving…' : 'Confirm'}
                </button>
                <button
                  onClick={() => { setResolving(false); setPendingUnmatched([]); setAssociations({}); }}
                  className="inline-flex h-9 items-center rounded-lg border border-slate-300 px-4 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {(invoiceDetailLoading || invoiceDetail) && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
            <h2 className="flex-1 text-base font-semibold text-slate-900">Invoice Detail</h2>
            {invoiceDetail && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                invoiceDetail.status === 'Paid' ? 'bg-green-100 text-green-700' :
                invoiceDetail.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {invoiceDetail.status}
              </span>
            )}
          </div>

          {invoiceDetailLoading ? (
            <div className="px-5 py-8 text-center text-sm text-slate-500">Loading…</div>
          ) : invoiceDetail && (
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <div className="text-xs text-slate-500">Reference</div>
                  <div className="mt-0.5 text-sm font-medium text-slate-900">{invoiceDetail.reference}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Dated</div>
                  <div className="mt-0.5 text-sm text-slate-900">{invoiceDetail.dated_on}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Due</div>
                  <div className="mt-0.5 text-sm text-slate-900">{invoiceDetail.due_on}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Payment terms</div>
                  <div className="mt-0.5 text-sm text-slate-900">{invoiceDetail.payment_terms_in_days} days</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <div className="text-xs text-slate-500">Net</div>
                  <div className="mt-0.5 text-sm font-medium text-slate-900">{fmt(invoiceDetail.net_value)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">VAT</div>
                  <div className="mt-0.5 text-sm text-slate-900">{fmt(invoiceDetail.sales_tax_value)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Total</div>
                  <div className="mt-0.5 text-sm font-semibold text-slate-900">{fmt(invoiceDetail.total_value)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Outstanding</div>
                  <div className={`mt-0.5 text-sm font-medium ${Number(invoiceDetail.due_value) > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {fmt(invoiceDetail.due_value)}
                  </div>
                </div>
              </div>

              {invoiceDetail.comments && (
                <div>
                  <div className="text-xs text-slate-500">Comments</div>
                  <div className="mt-0.5 text-sm text-slate-700">{invoiceDetail.comments}</div>
                </div>
              )}

              {invoiceDetail.invoice_items?.length > 0 && (
                <div>
                  <div className="text-xs text-slate-500 mb-2">Line items</div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-4 py-2 font-medium">Description</th>
                          <th className="px-4 py-2 font-medium text-right">Qty</th>
                          <th className="px-4 py-2 font-medium text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {invoiceDetail.invoice_items.map((item, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="px-4 py-2 text-slate-800">{item.description}</td>
                            <td className="px-4 py-2 text-right text-slate-700">{item.quantity}</td>
                            <td className="px-4 py-2 text-right text-slate-700">{fmt(item.price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
